import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { useWhatsAppSessionQuery } from '@/modules/whatsapp/api/whatsapp.api';
import { useAutomationQuery } from '../api/crm.api';

/**
 * What a person can actually do something about, and nothing else.
 *
 * Every line here is phrased as a state of the shop rather than a state of the software: "this
 * branch's WhatsApp needs re-authenticating", not "401". The codes are real and useful in the
 * detail drawer; on a workspace banner they only tell staff to go and find somebody technical.
 *
 * The runtime state comes from the session read model, which reads `WhatsAppSession` rows. The
 * configuration row carries a `sessionHealth` mirror that nothing updates when a socket connects —
 * it read NOT_CONFIGURED throughout a live send that worked — so nothing here consults it.
 */
export function OperationalHealth({ branchId }: { branchId: string | null }) {
  const { t } = useTranslation();
  const session = useWhatsAppSessionQuery(branchId, false);
  const automation = useAutomationQuery(null);

  if (!branchId || !session.data) return null;

  const active =
    session.data.activeIdentity === 'PRIMARY' ? session.data.primary : session.data.alternative;
  const automationOn = automation.data?.enabled ?? false;

  const settingsLink = (
    <Link className="text-brand underline" to="/settings/whatsapp">
      {t('crm.health.openSettings')}
    </Link>
  );

  if (active.requiresReauth || active.status === 'LOGGED_OUT') {
    return (
      <Alert tone="error">
        <p>{t('crm.health.reauthRequired')}</p>
        <p className="mt-1">{settingsLink}</p>
      </Alert>
    );
  }

  if (active.status === 'RESTRICTED') {
    return (
      <Alert tone="error">
        <p>{t('crm.health.restricted')}</p>
        <p className="mt-1">{settingsLink}</p>
      </Alert>
    );
  }

  // Automation switched on and the number it would send from is not usable. The automation is not
  // broken and is not reported as disabled — it is configured and currently cannot run.
  if (automationOn && !active.usable) {
    return (
      <Alert tone="warning">
        <p>{t('crm.health.automationWaiting')}</p>
        <p className="mt-1">{settingsLink}</p>
      </Alert>
    );
  }

  return null;
}
